import { Slot } from 'radix-ui'
import type * as React from 'react'

import { cn } from '@/utils/cn'

type CardSize = 'default' | 'sm' | 'compact'
type CardVariant = 'default' | 'muted' | 'dashed' | 'floating' | 'ghost'
type CardTone = 'neutral' | 'primary' | 'success' | 'warning' | 'destructive'

type CardProps = React.ComponentProps<'div'> & {
  size?: CardSize
  variant?: CardVariant
  tone?: CardTone
  interactive?: boolean
  selected?: boolean
  asChild?: boolean
}

const variantClasses: Record<CardVariant, string> = {
  default: 'border border-border bg-card',
  muted: 'border border-border/60 bg-muted text-foreground',
  dashed: 'border border-dashed border-border/60 bg-card/40',
  floating: 'border border-border/60 bg-card/95 shadow-xl backdrop-blur',
  ghost: 'border border-border bg-transparent',
}

const toneClasses: Record<CardTone, string> = {
  neutral: '',
  primary: 'border-primary/40 bg-primary/5',
  success: 'border-success/40 bg-success/5',
  warning: 'border-warning/40 bg-warning/5',
  destructive: 'border-destructive/40 bg-destructive/5',
}

function Card({
  className,
  size = 'default',
  variant = 'default',
  tone = 'neutral',
  interactive = false,
  selected = false,
  asChild = false,
  ...props
}: CardProps) {
  const Comp = asChild ? Slot.Root : 'div'
  return (
    <Comp
      data-slot="card"
      data-size={size}
      data-variant={variant}
      data-tone={tone}
      data-interactive={interactive ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      className={cn(
        'group/card rounded-xl py-6 text-sm text-card-foreground has-data-[slot=card-footer]:pb-0 has-[>img]:overflow-hidden has-[>img:first-child]:pt-0 *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl',
        // Stack layout only kicks in when the Card uses semantic slot children
        '[&:has(>[data-slot=card-header]),&:has(>[data-slot=card-content]),&:has(>[data-slot=card-footer])]:flex [&:has(>[data-slot=card-header]),&:has(>[data-slot=card-content]),&:has(>[data-slot=card-footer])]:flex-col [&:has(>[data-slot=card-header]),&:has(>[data-slot=card-content]),&:has(>[data-slot=card-footer])]:gap-6',
        'data-[size=sm]:py-5 data-[size=sm]:[&:has(>[data-slot=card-header]),&:has(>[data-slot=card-content]),&:has(>[data-slot=card-footer])]:gap-5',
        'data-[size=compact]:rounded-lg data-[size=compact]:py-3 data-[size=compact]:[&:has(>[data-slot=card-header]),&:has(>[data-slot=card-content]),&:has(>[data-slot=card-footer])]:gap-2',
        variantClasses[variant],
        toneClasses[tone],
        interactive &&
          'cursor-pointer transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        selected && 'border-primary ring-1 ring-primary/40',
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'group/card-header @container/card-header grid auto-rows-min items-start gap-2 rounded-t-xl px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-6',
        'group-data-[size=sm]/card:px-5 group-data-[size=sm]/card:[.border-b]:pb-5',
        'group-data-[size=compact]/card:px-3 group-data-[size=compact]/card:gap-1 group-data-[size=compact]/card:[.border-b]:pb-3',
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-lg leading-snug font-medium group-data-[size=sm]/card:text-base group-data-[size=compact]/card:text-sm group-data-[size=compact]/card:font-medium',
        className
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn(
        'text-sm text-muted-foreground group-data-[size=compact]/card:text-xs',
        className
      )}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn(
        'px-6 group-data-[size=sm]/card:px-5 group-data-[size=compact]/card:px-3',
        className
      )}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex items-center rounded-b-xl border-t border-border/30 bg-muted/10 px-6 py-5',
        'group-data-[size=sm]/card:px-5 group-data-[size=sm]/card:py-4',
        'group-data-[size=compact]/card:px-3 group-data-[size=compact]/card:py-2 group-data-[size=compact]/card:rounded-b-lg',
        className
      )}
      {...props}
    />
  )
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  type CardProps,
  CardTitle,
}
